import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, HardHatIcon, FileCheckIcon, CreditCardIcon, AlertCircleIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
type Status = 'Nouvelle demande' | 'En analyse' | 'En attente de documents' | 'Approuvé' | 'Refusé' | 'Émis';
interface Permit {
  id: string;
  type: string;
  category: string;
  permitId: string;
  location: string;
  date: string;
  status: Status;
  value: string;
  requester: {
    name: string;
    initials: string;
    color: string;
    phone: string;
    email: string;
    type: string;
  };
  contractor?: {
    name: string;
    rbq: string;
  };
}
const MOCK_PERMITS: Permit[] = [{
  id: '1',
  type: 'Construction neuve - Résidentiel',
  category: 'Construction',
  permitId: 'P-2026-142',
  location: '152, rue St-Georges, Windsor',
  date: '8 juin 2026',
  status: 'En analyse',
  value: '450 000 $',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]',
    phone: '(613) 446-1157',
    email: 'nouellet@email.com',
    type: 'Propriétaire'
  },
  contractor: {
    name: 'Constructions Tremblay inc.',
    rbq: '8321-4567-89'
  }
}, {
  id: '2',
  type: 'Agrandissement principal',
  category: 'Rénovation',
  permitId: 'P-2026-141',
  location: '205, rue Georges-Guilbault, Windsor',
  date: '7 juin 2026',
  status: 'En attente de documents',
  value: '85 000 $',
  requester: {
    name: 'Marc Lavoie',
    initials: 'ML',
    color: 'bg-emerald-500',
    phone: '(613) 222-3344',
    email: 'mlavoie@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '3',
  type: "Abattage d'arbre",
  category: 'Aménagement paysager',
  permitId: 'P-2026-140',
  location: '290, rue Principale Nord, Windsor',
  date: '6 juin 2026',
  status: 'Émis',
  value: '1 200 $',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-amber-500',
    phone: '(613) 555-6677',
    email: 'cnadeau@email.com',
    type: 'Locataire'
  },
  contractor: {
    name: 'Arboriculture Pro',
    rbq: 'N/A'
  }
}, {
  id: '4',
  type: 'Installation de piscine creusée',
  category: 'Aménagement extérieur',
  permitId: 'P-2026-139',
  location: '32, rue Ambroise-Dearden, Windsor',
  date: '5 juin 2026',
  status: 'Approuvé',
  value: '45 000 $',
  requester: {
    name: 'Jean Dubois',
    initials: 'JD',
    color: 'bg-blue-500',
    phone: '(613) 999-8877',
    email: 'jdubois@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '5',
  type: 'Rénovation de façade',
  category: 'Rénovation',
  permitId: 'P-2026-138',
  location: '70, 3e Avenue, Windsor',
  date: '4 juin 2026',
  status: 'Nouvelle demande',
  value: '25 000 $',
  requester: {
    name: 'Sophie Tremblay',
    initials: 'ST',
    color: 'bg-purple-500',
    phone: '(613) 111-2233',
    email: 'stremblay@email.com',
    type: 'Propriétaire'
  }
}];
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En analyse':
      return 'bg-indigo-100 text-indigo-700';
    case 'En attente de documents':
      return 'bg-amber-100 text-amber-700';
    case 'Approuvé':
      return 'bg-emerald-100 text-emerald-700';
    case 'Émis':
      return 'bg-blue-100 text-blue-700';
    case 'Refusé':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En analyse':
      return 'bg-[#4F46E5]';
    case 'En attente de documents':
      return 'bg-amber-500';
    case 'Approuvé':
      return 'bg-emerald-500';
    case 'Émis':
      return 'bg-blue-500';
    case 'Refusé':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  permitId: string;
  type: string;
  category: string;
  position: [number, number];
}[] = [{
  permitId: 'P-2026-142',
  type: 'Construction neuve',
  category: 'Construction',
  position: [45.4528, -73.4622]
}, {
  permitId: 'P-2026-141',
  type: 'Agrandissement',
  category: 'Rénovation',
  position: [45.4468, -73.4688]
}, {
  permitId: 'P-2026-140',
  type: "Abattage d'arbre",
  category: 'Aménagement',
  position: [45.4552, -73.4578]
}];
// Custom indigo map marker
const permitMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
type RightTab = 'gestion' | 'collaboration' | 'historique';
export function PermisGerer() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  if (selectedPermit) {
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-4848" data-mp="vejnl8bmw7qe">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-4849" data-mp="10btllcd39rpu">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-4850" data-mp="15by2v4ltmqh4">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-4851" data-mp="1a9zn3suzgvu6">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-4852" data-mp="cp3ngm3v2hai"></div>
            </div>
            Visible par le citoyen (Portail)
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-4853" data-mp="mlzu0ma78tcf">
            <button className="pb-3 border-b-2 border-[#4F46E5] text-[#4F46E5] font-semibold text-sm" data-id="element-4854" data-mp="mjvh5eubok00">
              Infos
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-4855" data-mp="3q1dq5pltzx20">
              Communications
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-4856" data-mp="2c6ov5dx8sj4r">
            <div className="flex justify-between items-start mb-8" data-id="element-4857" data-mp="2c42orhm389o4">
              <div className="flex gap-4" data-id="element-4858" data-mp="285boj1mbsbws">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-4859" data-mp="235qn8kjonrgw">
                  <FileCheckIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-4860" data-mp="1y5kvl4dey4yt" />
                </div>
                <div data-id="element-4861" data-mp="1t7t5mfwbwsx9">
                  <div className="flex items-center gap-2" data-id="element-4862" data-mp="1t945w5qkfijm">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-4863" data-mp="1ogazldfg825f">
                      {selectedPermit.type}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-4864" data-mp="1kfr2g9uvkyja" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-4865" data-mp="1fhza5eqbjaek">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200 text-slate-700" data-id="element-4866" data-mp="1agjobkgbgzv4">
                      {selectedPermit.permitId}
                    </span>
                    <span data-id="element-4867" data-mp="2ucxkzuf892xv">{selectedPermit.category}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-4868" data-mp="2ycg4qmvbb7z4">
                    Déposée le {selectedPermit.date}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(selectedPermit.status)}`} data-id="element-4869" data-mp="2pb8dgor7r7ej">
                <div className={`w-2 h-2 rounded-full ${getStatusDot(selectedPermit.status)}`} data-id="element-4870" data-mp="2ffoqwsyjhl2g"></div>
                {selectedPermit.status}
              </div>
            </div>

            <div className="mb-8" data-id="element-4871" data-mp="26n3ervpeycml">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4872" data-mp="1rpawivshfa4p">
                DESCRIPTION DES TRAVAUX
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-4873" data-mp="1wn2rxzsc5edn">
                Construction d'une maison unifamiliale isolée de 2 étages avec
                garage intégré. Revêtement extérieur en brique et déclin de
                bois. Aménagement paysager complet incluant l'entrée d'auto en
                pavé uni.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-4874" data-mp="1i0dr9umckv5o">
              <div data-id="element-4875" data-mp="1w0oiiyrkkbrd">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4876" data-mp="1w059k7jwcxph">
                  REQUÉRANT
                </h3>
                <div className="flex items-start gap-3" data-id="element-4877" data-mp="25r9ox8dhikku">
                  <div className={`w-8 h-8 rounded-full ${selectedPermit.requester.color} text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`} data-id="element-4878" data-mp="25qrn7w81ze5j">
                    {selectedPermit.requester.initials}
                  </div>
                  <div data-id="element-4879" data-mp="2arxfxu1pr1f2">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4880" data-mp="2ao9n6x43ej1e">
                      {selectedPermit.requester.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 bg-slate-100 inline-block px-1.5 py-0.5 rounded" data-id="element-4881" data-mp="2nvbzt1pdaul0">
                      {selectedPermit.requester.type}
                    </div>
                    <div className="text-sm text-slate-500 mt-1.5" data-id="element-4882" data-mp="2ewhyiy13dg14">
                      {selectedPermit.requester.phone}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4883" data-mp="3bjv5d9h18t9e">
                      {selectedPermit.requester.email}
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-4884" data-mp="32n466o76oyal">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4885" data-mp="2xmgcfurkm3co">
                  EMPLACEMENT & VALEUR
                </h3>
                <div className="flex items-start gap-2 mb-3" data-id="element-4886" data-mp="1r7fifum3vv8q">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4887" data-mp="1m6rop16ht0at" />
                  <div data-id="element-4888" data-mp="1pijbkteeauj">
                    <div className="text-sm font-medium text-slate-900" data-id="element-4889" data-mp="1qa4e1pdcq6k">
                      {selectedPermit.location.split(',')[0]}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4890" data-mp="apx6kppxa00o">Windsor, QC</div>
                    <button className="text-xs text-[#4F46E5] mt-1 font-medium hover:underline" data-id="element-4891" data-mp="anu8rjij32le">
                      Voir la sur la carte
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-4892" data-mp="pekz0n4h1tme">
                  <span className="text-slate-500" data-id="element-4893" data-mp="pcqfnd8um9kf">Valeur estimée:</span>
                  <span className="font-semibold text-slate-900" data-id="element-4894" data-mp="uepfwe9akqd5">
                    {selectedPermit.value}
                  </span>
                </div>
              </div>
            </div>

            {selectedPermit.contractor && <div className="mb-8 p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-4895" data-mp="2w73up4gjhepc">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-4896" data-mp="306w6rr5562zg">
                  <HardHatIcon className="w-4 h-4" data-id="element-4897" data-mp="2d199liseav4" /> ENTREPRENEUR
                </h3>
                <div className="flex justify-between items-center" data-id="element-4898" data-mp="2f5q141sb4ct">
                  <div data-id="element-4899" data-mp="gdlsgism68p5">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4900" data-mp="gaptcr515dz4">
                      {selectedPermit.contractor.name}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4901" data-mp="q9ykzdcfagib">
                      Licence RBQ: {selectedPermit.contractor.rbq}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded border border-emerald-200 flex items-center gap-1" data-id="element-4902" data-mp="101bba0ikoym8">
                    <CheckCircle2Icon className="w-3 h-3" data-id="element-4903" data-mp="v3tch2y9rzry" /> RBQ Valide
                  </span>
                </div>
              </div>}

            <div data-id="element-4904" data-mp="1436ejak2dm2n">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4905" data-mp="143puilq5m0e1">
                DOCUMENTS JUSTIFICATIFS SOUMIS
              </h3>
              <div className="space-y-3" data-id="element-4906" data-mp="1sn1c54lckqir">
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4907" data-mp="1slykuvk44m2b">
                  <div className="flex items-center gap-3" data-id="element-4908" data-mp="1ioc381bfigen">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4909" data-mp="14xp78gx1z4m9">
                      <FileTextIcon className="w-5 h-5" data-id="element-4910" data-mp="19o5mm6kfp0b9" />
                    </div>
                    <div data-id="element-4911" data-mp="uzj6i2mzg4iz">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4912" data-mp="v018lfwbtcyt">
                        Plans d'architecture complets
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4913" data-mp="lyl1bh0t5qyt">
                        PDF • 4.2 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4914" data-mp="ixrlx8x0gujj">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4915" data-mp="izcg2xfeybgv">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4916" data-mp="952dyo0o5ysy">
                  <div className="flex items-center gap-3" data-id="element-4917" data-mp="81u8uojoovu">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4918" data-mp="3rcs2b5mw2erf">
                      <FileTextIcon className="w-5 h-5" data-id="element-4919" data-mp="3meq93rvr0z28" />
                    </div>
                    <div data-id="element-4920" data-mp="3id540spghp2l">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4921" data-mp="3idy1f32iz282">
                        Certificat de localisation
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4922" data-mp="1gli60775ctwm">
                        PDF • 1.1 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4923" data-mp="33gf0kpyc3jzo">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4924" data-mp="388ook0fw29zb">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 cursor-pointer group" data-id="element-4925" data-mp="2fkalh69y0ngw">
                  <div className="flex items-center gap-3" data-id="element-4926" data-mp="2kjdsuxdeb5s1">
                    <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center text-red-600" data-id="element-4927" data-mp="2ojxq00xyy9e6">
                      <AlertCircleIcon className="w-5 h-5" data-id="element-4928" data-mp="2tklfgi6jui6a" />
                    </div>
                    <div data-id="element-4929" data-mp="3uq8yozkt9vi">
                      <div className="text-sm font-semibold text-red-900" data-id="element-4930" data-mp="3tf38jmkls1r">
                        Soumission détaillée de l'entrepreneur
                      </div>
                      <div className="text-xs text-red-700" data-id="element-4931" data-mp="24vwpp7apglpg">
                        Manquant • Requis
                      </div>
                    </div>
                  </div>
                  <button className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded transition-colors" data-id="element-4932" data-mp="2eulx264z6nrg">
                    Demander au citoyen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-4933" data-mp="1h7mx9mizkzpc">
          <button onClick={() => setSelectedPermit(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-4934" data-mp="1vvrioy9fbpbt">
            <XIcon className="w-5 h-5" data-id="element-4935" data-mp="13ex9bcjwdkpl" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-4936" data-mp="13ddrvimi93m8">
            <div className="flex items-center gap-2" data-id="element-4937" data-mp="3a37fmz8t6uit">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-4938" data-mp="3a2ecs9cz0p5y">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-4939" data-mp="3ewilax1vx595"></div>
              </div>
              Interne — Traitement de la demande
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-4940" data-mp="355nzftsjw7o8" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-4941" data-mp="3sugpxaqelnbg">
            <button onClick={() => setRightTab('gestion')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'gestion' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4942" data-mp="1nxg9ywb03eq">
              Gestion
            </button>
            <button onClick={() => setRightTab('collaboration')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'collaboration' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4943" data-mp="28e3wc9q35q7s">
              Collaboration
            </button>
            <button onClick={() => setRightTab('historique')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'historique' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4944" data-mp="304b1pos0v8j8">
              Historique
            </button>
          </div>

          {rightTab === 'gestion' && <div className="p-6 overflow-y-auto flex-1 space-y-8" data-id="element-4945" data-mp="3iusg0oc3y1l2">
              {/* Workflow — Horizontal Stepper Chronology */}
              <div data-id="element-4946" data-mp="3934wd9lslou5">
                <div className="flex items-center justify-between mb-4" data-id="element-4947" data-mp="391lg3j04zdsd">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4948" data-mp="ad4phq81miep">
                    PROCESSUS
                  </h3>
                  <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded border border-slate-200" data-id="element-4949" data-mp="5kbh8sdd8rxv">
                    Délai légal: 30 jours
                  </span>
                </div>

                <div className="flex items-start" data-id="element-4950" data-mp="hy89a3u72qtl">
                  {[{
                label: 'Réception',
                sub: 'Vérification préliminaire',
                meta: '8 juin 2026',
                state: 'done'
              }, {
                label: 'Analyse réglementaire',
                sub: 'Zonage · marges · hauteur',
                meta: 'En cours',
                state: 'current'
              }, {
                label: 'Approbation PIIA',
                sub: 'Comité (CCU)',
                meta: 'À venir',
                state: 'upcoming'
              }, {
                label: 'Émission',
                sub: 'Paiement · délivrance',
                meta: 'À venir',
                state: 'upcoming'
              }].map((step, i, arr) => {
                const isLast = i === arr.length - 1;
                const dotClass = step.state === 'done' ? 'bg-emerald-500 text-white border-emerald-500' : step.state === 'current' ? 'bg-[#4F46E5] text-white border-[#4F46E5] ring-4 ring-indigo-50' : 'bg-white text-slate-400 border-slate-300';
                return <div key={step.label} className="flex-1 flex flex-col items-center text-center" data-id="element-4951" data-mp="3brldmdazbucc" data-mpi={i}>
                        <div className="flex items-center w-full" data-id="element-4952" data-mp="3krgsot7no9hn">
                          <div className="flex-1 h-0.5" data-id="element-4953" data-mp="3kp43pw3hcyv3">
                            {i !== 0 && <div className={`h-0.5 ${step.state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4954" data-mp="3pnp7u6lus2ot"></div>}
                          </div>
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dotClass}`} data-id="element-4955" data-mp="1531gx1x42bcw">
                            {step.state === 'done' ? <CheckCircle2Icon className="w-4 h-4" data-id="element-4956" data-mp="1wv0ka9cl0gfk" /> : step.state === 'current' ? <ClockIcon className="w-4 h-4" data-id="element-4957" data-mp="1o0cf0j70ayqr" /> : <span className="text-xs font-bold" data-id="element-4958" data-mp="1j2b0mbkiznlm">{i + 1}</span>}
                          </div>
                          <div className="flex-1 h-0.5" data-id="element-4959" data-mp="2giq49uea1ce8">
                            {!isLast && <div className={`h-0.5 ${arr[i + 1].state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4960" data-mp="2bovmiwi8f7hs"></div>}
                          </div>
                        </div>
                        <div className="mt-2 px-1" data-id="element-4961" data-mp="2ltc1lnnw0kkj">
                          <div className={`text-xs font-semibold leading-tight ${step.state === 'upcoming' ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-4962" data-mp="2pvz18aqsv9sa">
                            {step.label}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 leading-tight" data-id="element-4963" data-mp="2gt7lhyqw5ruu">
                            {step.sub}
                          </div>
                          <div className={`text-[10px] mt-1 font-medium ${step.state === 'current' ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-4964" data-mp="26y5v6zkvqjqo">
                            {step.meta}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Current step regulatory analysis detail */}
                <div className="mt-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-4965" data-mp="1lkdsrlibktvz">
                  <div className="flex items-center justify-between mb-3" data-id="element-4966" data-mp="1zfg8f0cyxxlz">
                    <div className="flex items-center gap-2" data-id="element-4967" data-mp="1ulkb804j4erv">
                      <div className="w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4968" data-mp="29azzn9gl884e">
                        JD
                      </div>
                      <span className="text-xs font-medium text-slate-700" data-id="element-4969" data-mp="2icpqad2nb314">
                        Analyse réglementaire — Jacques Dubois (Urbanisme)
                      </span>
                    </div>
                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded" data-id="element-4970" data-mp="nxn870fxen88">
                      En cours
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-4971" data-mp="lejuqzkq2x72">
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4972" data-mp="ldqvsks09bzh">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4973" data-mp="pe0zfqsbrqb6" />
                      Zonage résidentiel H-123 conforme
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4974" data-mp="1nybnmvg16id">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4975" data-mp="6pdtz768kyx2" />
                      Marges de recul conformes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-900 font-medium" data-id="element-4976" data-mp="3f33a3e1vntil">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4977" data-mp="3k1nzeiflcss9" />
                      Hauteur maximale respectée
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditions & Exigences */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4978" data-mp="2xmfjvk3jd40p">
                <div className="flex items-center justify-between mb-4" data-id="element-4979" data-mp="2tlvmqgiyq0ek">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4980" data-mp="2oux6fgxz52bb">
                    CONDITIONS À JOINDRE AU PERMIS
                  </h3>
                  <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4981" data-mp="2jw3p1dfkgr5v">
                    + Ajouter
                  </button>
                </div>
                <div className="space-y-2" data-id="element-4982" data-mp="29wlaxdmc7zlq">
                  <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-100" data-id="element-4983" data-mp="29zh5cryz865f">
                    <AlertCircleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-4984" data-mp="1vaxo1kor2gz6" />
                    <p className="text-sm text-amber-900" data-id="element-4985" data-mp="20a0t354qcn75">
                      L'entrepreneur doit s'assurer de ne pas obstruer la voie
                      publique pendant les travaux.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded border border-slate-200" data-id="element-4986" data-mp="1r8kkn2xh6v5k">
                    <AlertCircleIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4987" data-mp="2ezzpb2cq5ojz" />
                    <p className="text-sm text-slate-600" data-id="element-4988" data-mp="2izgtlsgf0fnk">
                      Une inspection des fondations est requise avant le
                      remblayage.
                    </p>
                  </div>
                </div>
              </div>

              {/* Internal Files */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4989" data-mp="2xxiyc5z6n76e">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4990" data-mp="32o97luaqbu6d">
                  DOCUMENTS INTERNES (NON VISIBLES AU CITOYEN)
                </h3>
                <div className="flex gap-3" data-id="element-4991" data-mp="3c0yjil2b2n26">
                  <div className="w-24 rounded-lg border border-slate-200 p-3 flex flex-col items-center justify-center text-center hover:border-[#4F46E5] cursor-pointer transition-colors group" data-id="element-4992" data-mp="380odj8eijwnb">
                    <FileTextIcon className="w-8 h-8 text-indigo-300 group-hover:text-[#4F46E5] mb-2 transition-colors" data-id="element-4993" data-mp="3gv2o4xo3mj03" />
                    <div className="text-[10px] font-medium text-slate-700 w-full leading-tight" data-id="element-4994" data-mp="2t4yvflcg83nv">
                      Grille d'analyse réglementaire
                    </div>
                  </div>
                  <button className="w-24 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-4995" data-mp="3347n27jud672">
                    <PlusIcon className="w-5 h-5 mb-1" data-id="element-4996" data-mp="iei7fsbbdgdb" />
                    <span className="text-xs font-medium" data-id="element-4997" data-mp="dc9gf1i04odu">Générer</span>
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200" data-id="element-4998" data-mp="2bummiuqnvy1n">
                <button className="flex-1 bg-[#0F172A] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors" data-id="element-4999" data-mp="2bss3xn2v4i0q">
                  Approuver l'étape actuelle
                </button>
                <button className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors" data-id="element-5000" data-mp="2qkantvyuewpd">
                  Mettre en attente
                </button>
              </div>
            </div>}

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-5001" data-mp="34k1zhq1w0107">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5002" data-mp="156pipsu8i2gy">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5003" data-mp="158sdeq6c0js0">
                  INTERVENANTS ASSIGNÉS
                </h3>
                <div className="space-y-3" data-id="element-5004" data-mp="12nb7mo7u1vmz">
                  <div className="flex items-center justify-between" data-id="element-5005" data-mp="12o2v1bra0n26">
                    <div className="flex items-center gap-3" data-id="element-5006" data-mp="xtynore3md0k">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5007" data-mp="svx0pvdjxtjt">
                        JD
                      </div>
                      <div data-id="element-5008" data-mp="1llniqfib1fl7">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5009" data-mp="1ljszd5molvj8">
                          Jacques Dubois
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5010" data-mp="1bmxyig2xrj1s">
                          Urbanisme · Responsable
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100" data-id="element-5011" data-mp="7sv19uzdgh4">
                      Actif
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5012" data-mp="2r2of99ymbn60">
                    <div className="flex items-center gap-3" data-id="element-5013" data-mp="2r0v5vmwmeoub">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5014" data-mp="2m5655g8v71wf">
                        ST
                      </div>
                      <div data-id="element-5015" data-mp="2w196ijvw6wib">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5016" data-mp="2vzy34l6czqrq">
                          Sylvie Tremblay
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5017" data-mp="35slzjetffspx">
                          Réception · Collaboratrice
                        </div>
                      </div>
                    </div>
                    <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5018" data-mp="3es90jzi8uwig">
                      Voir
                    </button>
                  </div>
                </div>
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5019" data-mp="1kfatdorucfzc">
                  <PlusIcon className="w-4 h-4" data-id="element-5020" data-mp="3w35urassxe00" /> Ajouter un intervenant
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5021" data-mp="4us1qp32wa1b">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5022" data-mp="3mb9z06e6tzyy">
                  NOTES INTERNES
                </h3>
                <div className="space-y-3" data-id="element-5023" data-mp="3dbd43n17g4vg">
                  <div className="flex gap-3" data-id="element-5024" data-mp="3dak5xagbanox">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5025" data-mp="3iag9j8l3kd3y">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-5026" data-mp="38caacyqodl2s">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5027" data-mp="38d1sp7v9hmb4">
                        Marges de recul validées sur le certificat de
                        localisation. Reste à confirmer la hauteur au faîte.
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5028" data-mp="154rpj6ss97cv">
                        Jacques Dubois · il y a 2 h
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-5029" data-mp="v62he5qov19t">
                  <input type="text" placeholder="Ajouter une note interne..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5030" data-mp="r4h1scmrs8vx" />
                  <button className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-5031" data-mp="1e3n00v2v13lz">
                    Publier
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-5032" data-mp="21t7g967mpmrk">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5033" data-mp="3kh0lfvp4lu49">
                JOURNAL D'ACTIVITÉ
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-5034" data-mp="3pgn26f45xsht">
                <div className="relative pl-6" data-id="element-5035" data-mp="3tgdvoql2qmq1">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5036" data-mp="3tjryfzxngxy0"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5037" data-mp="7aqrhwqz4x0u">
                    Analyse réglementaire débutée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5038" data-mp="c7qutzwfvlo9">
                    Jacques Dubois (Urbanisme)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5039" data-mp="l1m1xdh4fm6j">
                    8 juin 2026 · 14:32
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5040" data-mp="q21isf0m3bt9">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-5041" data-mp="uzt9j5pisrvv"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5042" data-mp="hcax0uepggsz">
                    Documents vérifiés et conformes
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5043" data-mp="cbwzi6aqcp97">
                    Sylvie Tremblay (Réception)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5044" data-mp="m39i07an232k">
                    8 juin 2026 · 10:15
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5045" data-mp="3ujutbuqqt88u">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-5046" data-mp="3pl1cptg5t14g"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5047" data-mp="8d60tuqm8zjz">
                    Demande de permis déposée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5048" data-mp="3fnzapexhmm0">
                    Nathalie Ouellet (Portail citoyen)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5049" data-mp="1erf2csxs5q1v">
                    8 juin 2026 · 09:02
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-5050" data-mp="1r8zketkuht7k">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-5051" data-mp="1ddnowjyv7ypl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-5052" data-mp="1hdo6uel6as1e">
          <div className="flex items-center gap-3" data-id="element-5053" data-mp="253ikgydbgkmp">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-5054" data-mp="2a1kak395tk7o">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5055" data-mp="2a3ek6lezdpwg">
                <TableIcon className="w-4 h-4" data-id="element-5056" data-mp="2ss1qt8ct2nc5" />
                Tableau
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5057" data-mp="32o1w3n3ztpfp">
                <MapIcon className="w-4 h-4" data-id="element-5058" data-mp="2oo965u0ajd8s" />
                Carte
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-5059" data-mp="2joxrqrnecjud">
              142 demandes de permis
            </span>
          </div>

          <div className="flex items-center gap-3" data-id="element-5060" data-mp="2exekoruvchoe">
            <div className="relative" data-id="element-5061" data-mp="2ewmslr0hd22v">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5062" data-mp="2aw2qe911v89v" />
              <input type="text" placeholder="Rechercher un permis..." className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5063" data-mp="25yu7g682v07k" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-5064" data-mp="1luvcb9pmuli1">
              <FilterIcon className="w-4 h-4" data-id="element-5065" data-mp="1vtkwxa8wyk1z" />
              Filtrer
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-5066" data-mp="20tgzr65vk5fy">
              <PlusIcon className="w-4 h-4" data-id="element-5067" data-mp="1849u7n806fl7" />
              Nouveau permis
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-5068" data-mp="1hu32ae8xsckp">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-5069" data-mp="1hve62e2dtkbt">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-5070" data-mp="23k8sn40z0yla" />
            {MAP_MARKERS.map(m => <Marker key={m.permitId} position={m.position} icon={permitMarkerIcon} data-id="element-5071" data-mp="2dj7pzqgad7sz">
                <Popup data-id="element-5072" data-mp="2mj370bwivx0x">
                  <div className="text-sm" data-id="element-5073" data-mp="2mibbt26u81b6">
                    <div className="font-semibold text-slate-900" data-id="element-5074" data-mp="2mh9w2kevmqn4">
                      {m.permitId} — {m.type}
                    </div>
                    <div className="text-slate-500" data-id="element-5075" data-mp="2w8mobd4l7ito">{m.category}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-5076" data-mp="366apakssdmka">
        <table className="w-full text-left border-collapse" data-id="element-5077" data-mp="367c1wtpgah44">
          <thead data-id="element-5078" data-mp="31923894ioydr">
            <tr className="border-b border-slate-200 bg-white" data-id="element-5079" data-mp="319k8fv95qmxt">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5080" data-mp="3kt9esa8326wd">
                <span className="inline-flex items-center gap-1" data-id="element-5081" data-mp="3ovw6mb4r7rtk">
                  Type de permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5082" data-mp="3aweebls91wfg" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5083" data-mp="3ftelk00u5585">
                <span className="inline-flex items-center gap-1" data-id="element-5084" data-mp="314qqrvytczj9">
                  N° Permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5085" data-mp="35xbkkxhpzcms" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5086" data-mp="2s6yd0w8qpzxz">
                Emplacement
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5087" data-mp="2i77iwvo3dmyf">
                <span className="inline-flex items-center gap-1" data-id="element-5088" data-mp="2n5r5myaif2ru">
                  Date de dépôt{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5089" data-mp="3e7j3hq0e0s4a" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5090" data-mp="3j6lwud7099wf">
                <span className="inline-flex items-center gap-1" data-id="element-5091" data-mp="34i7tm15ofjfc">
                  Statut{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5092" data-mp="39fr9t6lxq69x" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5093" data-mp="2vfym7k5pg666">
                Demandeur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5094" data-mp="30di0ul6bekyn">
                Valeur
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5095" data-mp="2qi6sckli29zc">
            {MOCK_PERMITS.map(permit => <tr key={permit.id} onClick={() => setSelectedPermit(permit)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-5096" data-mp="js0ca9rpwn6a">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-5097" data-mp="298m449rruhom">
                  <div className="flex items-center gap-3" data-id="element-5098" data-mp="24auddj2v51m0">
                    <div className="w-8 h-8 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-5099" data-mp="2iynvbaff8g7r">
                      <FileCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-5100" data-mp="2e5uqynnv79w7" />
                    </div>
                    <div data-id="element-5101" data-mp="2rx99ktdv192q">
                      <div className="font-medium text-slate-900" data-id="element-5102" data-mp="2s0opfyfcym57">
                        {permit.type}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-5103" data-mp="31xjm0bs2mcgu">
                        {permit.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-5104" data-mp="17ih3wz5cpwny">
                  {permit.permitId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5105" data-mp="12m0f02qbve9t">
                  <div className="flex items-center gap-1.5" data-id="element-5106" data-mp="32nq90f76b2hd">
                    <MapPinIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5107" data-mp="37kglksjrrfy1" />
                    {permit.location.split(',')[0]}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5108" data-mp="3glwv6y2rfe17">
                  {permit.date}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-5109" data-mp="3ljye9j06rdco">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`} data-id="element-5110" data-mp="3qk4azdlbbq1m">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(permit.status)}`} data-id="element-5111" data-mp="3vd6usu0afl4m"></div>
                    {permit.status}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5112" data-mp="d4y629kgozfv">
                  <div className="flex items-center gap-2" data-id="element-5113" data-mp="n94tz9tc5zbc">
                    <div className={`w-6 h-6 rounded-full ${permit.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-5114" data-mp="napo4ybqng8o">
                      {permit.requester.initials}
                    </div>
                    {permit.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-5115" data-mp="1605ptoz5teqo">
                  {permit.value}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-5116" data-mp="w8042wzi3tjx">
        <div className="flex items-center gap-2" data-id="element-5117" data-mp="s6esdeypdlbb">
          Éléments par page
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-5118" data-mp="1orf1a3shqdkw">
            <option data-id="element-5119" data-mp="1jxkkb849scpi">10</option>
            <option data-id="element-5120" data-mp="14gumg3djiywv">20</option>
            <option data-id="element-5121" data-mp="14ft1962t8w1f">50</option>
          </select>
        </div>
        <div data-id="element-5122" data-mp="19h0al9klq3a7">1 - 10 / 142</div>
        <div className="flex items-center gap-4" data-id="element-5123" data-mp="19h9zsuxto6eu">
          <button className="hover:text-slate-900" data-id="element-5124" data-mp="vgpk47n7eqpk">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-5125" data-mp="viab5nab7rio" />
          </button>
          <button className="hover:text-slate-900" data-id="element-5126" data-mp="zhj5oud6btfa">
            <ChevronRightIcon className="w-4 h-4" data-id="element-5127" data-mp="zkdroqbu5mat" />
          </button>
        </div>
      </div>
    </div>;
}